// @flow

import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQuery, useQueryClient } from 'react-query';
import { actions as toastrActions, toastr } from 'react-redux-toastr';
import { formatDateDisplayUTC } from '@fortress-technology-solutions/fortress-component-library/utils/index';
import GrossRentsService from '../../../services/grossRentsService';
import { promptToaster } from '../../App/actions';
import generalMessages from '../../App/messages';
import usePermissionsHook from '../../../components/ElementWithPermissions/usePermissionsHook';
import useBatchCompleteGrossRentCertificationsMutation from './hooks.useBatchCompleteGrossRentCertificationsMutation';
import useDownloadAll59AsMutation from './hooks.useDownloadAll59AsMutation';
import useIntl from '../../../hooks/useIntl';
import useSelectedProperty from '../../../hooks/useSelectedProperty';
import { extractErrorMessage } from '../../../utils/errors';
import {
  determineIf59aIsDisabled,
  hasGrcGenerated59a,
  isGrcMissingPriorFullCertData,
} from './utils';
import parentMessages from '../messages';
import tabMessages from './messages';
import type {
  GrossRentCertification,
  GrossRentCertificationsResponse,
  Signed59ADocumentType,
} from './types';

const grossRentsService = new GrossRentsService();

const STALE_TIME_MS = 60_000;

const EMPTY_SIGNED_59A_DOCUMENT_TYPE: Signed59ADocumentType = {};

const MENU_OPTION_GENERATE_ALL = 'generateAll59As';
const MENU_OPTION_COMPLETE = 'completeCertifications';

const BATCH_COMPLETE_SCOPE = ['hud-grc-batch-complete'];

const COMPLETE_CERTIFICATIONS_IN_PROGRESS_TOAST_ID =
  'complete-gross-rent-certifications-in-progress';

const GENERATE_ALL_59AS_IN_PROGRESS_TOAST_ID = 'generate-all-59as-in-progress';

type TooltipMessageDescriptor = { id: string, defaultMessage: string };

const sortEffectiveDatesDesc = (dates: string[]): string[] =>
  [...dates].sort((left, right) => right.localeCompare(left));

const getUniqueEffectiveDates = (
  certifications: GrossRentCertification[],
): string[] => {
  const dates = certifications.flatMap(({ grEffectiveDate }) => {
    if (typeof grEffectiveDate === 'string' && grEffectiveDate.length > 0) {
      return [grEffectiveDate];
    }

    return [];
  });

  return sortEffectiveDatesDesc([...new Set(dates)]);
};

const formatEffectiveDateOption = (date: string): string =>
  formatDateDisplayUTC(date, 'MM/DD/YYYY');

export const getCertificationsEligibleForBatchCompletion = (
  certifications: GrossRentCertification[],
): GrossRentCertification[] => {
  return certifications.filter(
    (certification) => isGrcMissingPriorFullCertData(certification) === false,
  );
};

export const getCertificationsSubmittableForBatchCompletion = (
  certifications: GrossRentCertification[],
): GrossRentCertification[] => {
  return getCertificationsEligibleForBatchCompletion(certifications).filter(
    (certification) => hasGrcGenerated59a(certification),
  );
};

export const hasCertificationsWithMissingRequiredData = (
  certifications: GrossRentCertification[],
): boolean => {
  return certifications.some(
    (certification) => isGrcMissingPriorFullCertData(certification) === true,
  );
};

export const areAllCertificationsMissingRequiredData = (
  certifications: GrossRentCertification[],
): boolean => {
  return (
    certifications.length > 0 &&
    certifications.every(
      (certification) => isGrcMissingPriorFullCertData(certification) === true,
    )
  );
};

type GetManageGrossRentsMenuDisabledTooltipMessageOptions = {
  allCertifications: GrossRentCertification[],
  certifications: GrossRentCertification[],
};

export const getManageGrossRentsMenuDisabledTooltipMessage = ({
  allCertifications,
  certifications,
}: GetManageGrossRentsMenuDisabledTooltipMessageOptions): TooltipMessageDescriptor | null => {
  if (allCertifications.length === 0) {
    return tabMessages.noOpenGrossRentCertsTooltip;
  }

  if (areAllCertificationsMissingRequiredData(certifications)) {
    return tabMessages.menuDisabledMissingRequiredDataTooltip;
  }

  return null;
};

export const hasEligibleCertificationsMissingGenerated59a = (
  certifications: GrossRentCertification[],
): boolean => {
  return getCertificationsEligibleForBatchCompletion(certifications).some(
    (certification) => hasGrcGenerated59a(certification) === false,
  );
};

export const isCompleteCertificationsMenuEnabled = (
  certifications: GrossRentCertification[],
): boolean => {
  const eligibleCertifications =
    getCertificationsEligibleForBatchCompletion(certifications);

  if (eligibleCertifications.length === 0) {
    return false;
  }

  return hasEligibleCertificationsMissingGenerated59a(certifications) === false;
};

type GetCompleteCertificationsTooltipMessageOptions = {
  certifications: GrossRentCertification[],
  hasBatchCompletePermission: boolean,
};

export const getCompleteCertificationsTooltipMessage = ({
  certifications,
  hasBatchCompletePermission,
}: GetCompleteCertificationsTooltipMessageOptions): TooltipMessageDescriptor | null => {
  if (!hasBatchCompletePermission) {
    return parentMessages.noPermissionTooltip;
  }

  if (hasEligibleCertificationsMissingGenerated59a(certifications)) {
    return tabMessages.generateAll59AsBeforeCompletingTooltip;
  }

  return null;
};

// Back-compat alias for existing tests/callers.
export const allEligibleCertsHaveGenerated59 =
  isCompleteCertificationsMenuEnabled;

const useGrossRentCertifications = () => {
  const intl = useIntl();
  const selectedProperty = useSelectedProperty();
  const organizationId = selectedProperty?.organizationId ?? '';
  const propertyId = selectedProperty?.id ?? '';
  const queryKey = ['grossRentCertifications', organizationId, propertyId];
  const errorToastShownRef = useRef(false);

  useEffect(() => {
    errorToastShownRef.current = false;
  }, [organizationId, propertyId]);

  const queryOptions = useMemo(
    () => ({
      enabled: Boolean(organizationId && propertyId),
      refetchOnWindowFocus: false,
      staleTime: STALE_TIME_MS,
      onSuccess: () => {
        errorToastShownRef.current = false;
      },
      onError: (error: mixed) => {
        if (errorToastShownRef.current) {
          return;
        }
        errorToastShownRef.current = true;
        promptToaster({
          type: 'error',
          title: intl.formatMessage(tabMessages.errorLoading),
          message: extractErrorMessage(error),
        });
      },
    }),
    [organizationId, propertyId, intl],
  );

  const { data, isLoading, isSuccess, isError, isFetching, refetch } = useQuery(
    queryKey,
    async ({ signal }) => {
      const response = await grossRentsService.getGrossRentCertifications(
        organizationId,
        propertyId,
        { signal },
      );
      const responseData: GrossRentCertificationsResponse = response?.data ?? {
        grossRentCerts: [],
        signed59ADocumentType: EMPTY_SIGNED_59A_DOCUMENT_TYPE,
      };

      return responseData;
    },
    queryOptions,
  );

  const grossRentCertifications: GrossRentCertification[] =
    data?.grossRentCerts ?? [];
  const signed59ADocumentType: Signed59ADocumentType =
    data?.signed59ADocumentType ?? EMPTY_SIGNED_59A_DOCUMENT_TYPE;
  const isRetrying = isFetching && !isLoading;

  return {
    grossRentCertifications,
    signed59ADocumentType,
    isLoading,
    isSuccess,
    isError,
    isFetching,
    isRetrying,
    refetch,
  };
};

export const useEffectiveDateFilter = (
  certifications: GrossRentCertification[],
) => {
  const effectiveDateOptions = useMemo(
    () => getUniqueEffectiveDates(certifications),
    [certifications],
  );

  const [selectedEffectiveDate, setSelectedEffectiveDate] = useState('');

  useEffect(() => {
    if (effectiveDateOptions.length === 0) {
      setSelectedEffectiveDate('');
      return;
    }

    const selectedDateIsValid = effectiveDateOptions.includes(
      selectedEffectiveDate,
    );

    if (selectedDateIsValid === false) {
      setSelectedEffectiveDate(effectiveDateOptions[0]);
    }
  }, [effectiveDateOptions, selectedEffectiveDate]);

  const filteredCertifications = useMemo(() => {
    if (selectedEffectiveDate === '') {
      return certifications;
    }

    return certifications.filter(
      ({ grEffectiveDate }) => grEffectiveDate === selectedEffectiveDate,
    );
  }, [certifications, selectedEffectiveDate]);

  const selectOptions = useMemo(
    () =>
      effectiveDateOptions.map((date) => ({
        value: date,
        text: formatEffectiveDateOption(date),
      })),
    [effectiveDateOptions],
  );

  return {
    effectiveDateOptions,
    filteredCertifications,
    selectOptions,
    selectedEffectiveDate,
    setSelectedEffectiveDate,
  };
};

type UseManageGrossRentsMenuOptions = {
  allCertifications: GrossRentCertification[],
  certifications: GrossRentCertification[],
  intl: Object,
  refetch: () => void,
  selectedEffectiveDate: string,
};

export const useManageGrossRentsMenu = ({
  allCertifications,
  certifications,
  intl,
  refetch,
  selectedEffectiveDate,
}: UseManageGrossRentsMenuOptions) => {
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const selectedProperty = useSelectedProperty();
  const organizationId = selectedProperty?.organizationId ?? '';
  const propertyId = selectedProperty?.id ?? '';
  const hasBatchCompletePermission = usePermissionsHook({
    scope: BATCH_COMPLETE_SCOPE,
  });

  const downloadAll59AsMutation = useDownloadAll59AsMutation({
    organizationId,
    propertyId,
    // Scope the batch 59A to the batch the user is looking at (ENG-30783 Scenario 4).
    grEffectiveDate: selectedEffectiveDate,
    options: {
      onMutate: () => {
        dispatch(
          toastrActions.add({
            id: GENERATE_ALL_59AS_IN_PROGRESS_TOAST_ID,
            type: 'info',
            title: intl.formatMessage(tabMessages.generatingAll59As),
            message: intl.formatMessage(tabMessages.willDownloadWhenComplete),
            options: {
              timeOut: 0,
              removeOnHover: false,
              showCloseButton: true,
            },
          }),
        );
      },
      onSuccess: () => {
        dispatch(toastrActions.remove(GENERATE_ALL_59AS_IN_PROGRESS_TOAST_ID));
        toastr.success(
          intl.formatMessage(generalMessages.success),
          intl.formatMessage(tabMessages.generateAll59AsSuccess),
        );
        refetch();
      },
      onError: (error: mixed) => {
        dispatch(toastrActions.remove(GENERATE_ALL_59AS_IN_PROGRESS_TOAST_ID));
        toastr.error(
          intl.formatMessage(generalMessages.error),
          intl.formatMessage(tabMessages.errorGeneratingAll59As, {
            errorMessage: extractErrorMessage(error),
          }),
        );
      },
    },
  });

  const batchCompleteMutation = useBatchCompleteGrossRentCertificationsMutation(
    {
      organizationId,
      propertyId,
      options: {
        onMutate: () => {
          setIsCompleteModalOpen(false);
          dispatch(
            toastrActions.add({
              id: COMPLETE_CERTIFICATIONS_IN_PROGRESS_TOAST_ID,
              type: 'info',
              title: intl.formatMessage(
                tabMessages.completeCertificationsInProgressTitle,
              ),
              message: intl.formatMessage(
                tabMessages.completeCertificationsInProgressMessage,
              ),
              options: {
                timeOut: 0,
                className: 'rrt-purple',
                removeOnHover: false,
                showCloseButton: true,
              },
            }),
          );
        },
        onSuccess: () => {
          dispatch(
            toastrActions.remove(COMPLETE_CERTIFICATIONS_IN_PROGRESS_TOAST_ID),
          );
          queryClient.invalidateQueries([
            'grossRentCertifications',
            organizationId,
            propertyId,
          ]);
          queryClient.invalidateQueries([
            'grossRentChanges',
            organizationId,
            propertyId,
          ]);
          toastr.success(
            intl.formatMessage(generalMessages.success),
            intl.formatMessage(tabMessages.completeCertificationsSuccess),
          );
          refetch();
        },
        onError: (error: mixed) => {
          dispatch(
            toastrActions.remove(COMPLETE_CERTIFICATIONS_IN_PROGRESS_TOAST_ID),
          );
          toastr.error(
            intl.formatMessage(generalMessages.error),
            intl.formatMessage(tabMessages.errorCompletingCertifications, {
              errorMessage: extractErrorMessage(error),
            }),
          );
        },
      },
    },
  );

  const generatableCertifications = useMemo(
    () =>
      certifications.filter(
        (certification) => determineIf59aIsDisabled(certification) === false,
      ),
    [certifications],
  );

  const batchCompletableCertifications = useMemo(
    () => getCertificationsSubmittableForBatchCompletion(certifications),
    [certifications],
  );

  const hasMissingRequiredData = useMemo(
    () => hasCertificationsWithMissingRequiredData(certifications),
    [certifications],
  );

  const formattedEffectiveDate = useMemo(() => {
    if (selectedEffectiveDate === '') {
      return '';
    }

    return formatEffectiveDateOption(selectedEffectiveDate);
  }, [selectedEffectiveDate]);

  const menuDisabledTooltipMessage =
    getManageGrossRentsMenuDisabledTooltipMessage({
      allCertifications,
      certifications,
    });
  const isMenuDisabled = menuDisabledTooltipMessage !== null;
  const isGenerateAllDisabled = generatableCertifications.length === 0;
  const isCompleteBatchDisabledByBusinessRules =
    certifications.length === 0 ||
    isCompleteCertificationsMenuEnabled(certifications) === false;
  const isCompleteBatchDisabled =
    isCompleteBatchDisabledByBusinessRules || !hasBatchCompletePermission;
  const completeCertificationsTooltipMessage =
    getCompleteCertificationsTooltipMessage({
      certifications,
      hasBatchCompletePermission,
    });
  const isSubmittingGenerateAll = downloadAll59AsMutation.isLoading;
  const isSubmittingComplete = batchCompleteMutation.isLoading;

  const handleMenuItemClick = (value: string) => {
    if (value === MENU_OPTION_GENERATE_ALL) {
      if (isGenerateAllDisabled || isSubmittingGenerateAll) {
        return;
      }
      downloadAll59AsMutation.mutate();
      return;
    }

    if (value === MENU_OPTION_COMPLETE) {
      if (isCompleteBatchDisabled || isSubmittingComplete) {
        return;
      }
      setIsCompleteModalOpen(true);
    }
  };

  const handleCompleteModalCancel = () => {
    setIsCompleteModalOpen(false);
  };

  const handleCompleteModalConfirm = () => {
    if (isSubmittingComplete) {
      return;
    }

    const affordableQualificationIds = batchCompletableCertifications.map(
      ({ affordableQualificationId }) => affordableQualificationId,
    );

    batchCompleteMutation.mutate({ affordableQualificationIds });
  };

  const menuOptions = [
    {
      value: MENU_OPTION_GENERATE_ALL,
      text: intl.formatMessage(tabMessages.generateAll59As),
      disabled: isGenerateAllDisabled || isSubmittingGenerateAll,
    },
    {
      value: MENU_OPTION_COMPLETE,
      text: intl.formatMessage(tabMessages.completeCertifications),
      disabled: isCompleteBatchDisabled || isSubmittingComplete,
      tooltip: completeCertificationsTooltipMessage
        ? intl.formatMessage(completeCertificationsTooltipMessage)
        : undefined,
    },
  ];

  return {
    batchCompletableCertifications,
    completeModalProps: {
      effectiveDate: formattedEffectiveDate,
      hasMissingRequiredData,
      isSubmitting: isSubmittingComplete,
      onCancel: handleCompleteModalCancel,
      onConfirm: handleCompleteModalConfirm,
      open: isCompleteModalOpen,
    },
    handleMenuItemClick,
    isCompleteBatchDisabled,
    isGenerateAllDisabled,
    isMenuDisabled,
    menuDisabledTooltip: menuDisabledTooltipMessage
      ? intl.formatMessage(menuDisabledTooltipMessage)
      : undefined,
    isSubmittingComplete,
    isSubmittingGenerateAll,
    menuOptions,
  };
};

export default useGrossRentCertifications;
